import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Coins, Users, Activity, 
  TrendingUp, Truck, Wrench, ClipboardCheck, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS = [
  { app_id: 'ridero', name: 'Ridero', icon: Truck, color: '#3B82F6' },
  { app_id: 'mechanic_kiosk', name: 'MechanicKiosk', icon: Wrench, color: '#10B981' },
  { app_id: 'pti_pector', name: 'PTI Pector', icon: ClipboardCheck, color: '#F97316' },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/points/transactions`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setTransactions(transactionsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const isMasterAdmin = user?.role_id === 'master_admin';

  if (isLoading) {
    return (
      <DashboardLayout>
        <Header title="Dashboard" subtitle="Admin overview" />
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="skeleton-shimmer h-32" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title="Dashboard" subtitle={`Admin overview - ${user?.name}`} />
      
      <div className="p-6 space-y-6">
        {/* Stats Grid */}
        {stats?.admin && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="card-hover animate-fade-in-up animate-stagger-1" data-testid="total-users-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-3xl font-bold mt-1">{stats.admin.total_users}</p>
                    <p className="text-xs text-muted-foreground mt-1">{stats.admin.active_users} active</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover animate-fade-in-up animate-stagger-2" data-testid="pending-approvals-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Approval</p>
                    <p className="text-3xl font-bold mt-1">{stats.admin.pending_approval || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Need review</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover animate-fade-in-up animate-stagger-3" data-testid="total-events-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Events</p>
                    <p className="text-3xl font-bold mt-1">{stats.admin.total_events}</p>
                    <p className="text-xs text-muted-foreground mt-1">Across all apps</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Activity className="w-6 h-6 text-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover animate-fade-in-up animate-stagger-4" data-testid="total-points-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Points Issued</p>
                    <p className="text-3xl font-bold mt-1">{stats.admin.total_points_issued?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total distributed</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                    <Coins className="w-6 h-6" style={{ color: 'hsl(var(--success))' }} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover animate-fade-in-up animate-stagger-5" data-testid="your-points-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Your Points</p>
                    <p className="text-3xl font-bold mt-1">{user?.points_balance?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">Personal balance</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* App Stats */}
        {stats?.admin?.app_stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {APPS.map((app, idx) => {
              const appStats = stats.admin.app_stats[app.app_id] || {};
              const Icon = app.icon;
              
              return (
                <Card 
                  key={app.app_id} 
                  className={`app-card card-hover animate-fade-in-up animate-stagger-${idx + 1}`}
                  style={{ '--app-color': app.color }}
                  data-testid={`app-stat-${app.app_id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${app.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: app.color }} />
                      </div>
                      <CardTitle className="text-lg">{app.name}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold">{appStats.users || 0}</p>
                        <p className="text-xs text-muted-foreground">Users</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{appStats.events || 0}</p>
                        <p className="text-xs text-muted-foreground">Events</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{appStats.admins || 0}</p>
                        <p className="text-xs text-muted-foreground">Admins</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Recent Activity */}
        <Card className="animate-fade-in-up" data-testid="recent-activity-card">
          <CardHeader>
            <CardTitle className="font-secondary uppercase flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent" />
              Recent Activity
            </CardTitle>
            <CardDescription>Your latest point transactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transactions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No transactions yet.
                </p>
              ) : (
                transactions.map((txn, idx) => (
                  <div 
                    key={txn.transaction_id} 
                    className={`flex items-center justify-between p-3 rounded-lg bg-secondary/30 animate-fade-in-up animate-stagger-${idx + 1}`}
                  >
                    <div>
                      <p className="font-medium text-sm">{txn.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`font-semibold ${txn.amount >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {txn.amount >= 0 ? '+' : ''}{txn.amount}
                    </span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
